import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { formatRp, formatDateID, todayWITA } from "@/lib/constants";
import { Sparkle, House, Storefront, FileCsv, Printer } from "@phosphor-icons/react";

const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100";
function firstOfMonth() { const t = todayWITA(); return t.slice(0, 8) + "01"; }

function CompareCard({ title, icon: Icon, data, color, bg }) {
  return (
    <div className="rounded-2xl border border-rose-100/70 bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ backgroundColor: bg, color }}><Icon size={20} weight="duotone" /></span>
        <h3 className="font-heading text-lg font-bold text-slate-800">{title}</h3>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div><p className="text-xs text-slate-400">Transaksi</p><p className="font-heading text-xl font-extrabold text-slate-800">{data.transaksi}</p></div>
        <div><p className="text-xs text-slate-400">Klien</p><p className="font-heading text-xl font-extrabold text-slate-800">{data.klien}</p></div>
        <div><p className="text-xs text-slate-400">Total Omzet</p><p className="font-heading text-lg font-extrabold text-emerald-700">{formatRp(data.omzet)}</p></div>
        <div><p className="text-xs text-slate-400">Rata-rata</p><p className="font-heading text-lg font-extrabold text-slate-800">{formatRp(data.rata_rata)}</p></div>
      </div>
    </div>
  );
}

export default function SpaReport() {
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(todayWITA());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/reports/spa", { params: { start, end } }); setData(r.data); } finally { setLoading(false); }
  }, [start, end]);
  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    if (!data) return;
    const header = ["Terapis", "Lokasi", "Jumlah Pasien", "Jumlah Treatment", "Omzet", "Diskon", "Omzet Final"];
    const rows = data.rows.map((r) => [r.terapis, r.lokasi, r.jumlah_pasien, r.jumlah_treatment, r.omzet, r.diskon, r.omzet_final]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `Laporan_Spa_${start}_${end}.csv`; a.click();
  };

  return (
    <div className="animate-fade-up space-y-6">
      <div className="no-print flex items-center gap-2"><Sparkle size={26} weight="duotone" className="text-purple-500" /><h1 className="font-heading text-3xl font-extrabold text-slate-800">Laporan Spa & Mom/Baby Care</h1></div>

      <div className="no-print rounded-2xl border border-rose-100/70 bg-white p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div><label className="mb-1 block text-xs font-semibold text-slate-500">Dari</label><input type="date" data-testid="spa-start" value={start} onChange={(e) => setStart(e.target.value)} className={inputCls} /></div>
          <div><label className="mb-1 block text-xs font-semibold text-slate-500">Sampai</label><input type="date" data-testid="spa-end" value={end} onChange={(e) => setEnd(e.target.value)} className={inputCls} /></div>
          <div className="flex items-end gap-2">
            <button data-testid="spa-export" onClick={exportCSV} className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700"><FileCsv size={16} weight="duotone" /> CSV</button>
            <button data-testid="spa-print" onClick={() => window.print()} className="flex items-center gap-2 rounded-xl bg-slate-800 px-4 py-2 text-sm font-bold text-white hover:bg-slate-900"><Printer size={16} weight="duotone" /> Print</button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-4 border-purple-200 border-t-purple-500" /></div>
      ) : data ? (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <CompareCard title="Homecare" icon={House} data={data.homecare} color="#7c3aed" bg="#f3e8ff" />
            <CompareCard title="Gerai GBS" icon={Storefront} data={data.gerai} color="#0891b2" bg="#cffafe" />
          </div>

          <div>
            <h2 className="mb-3 font-heading text-xl font-bold text-slate-800">Kinerja per Terapis</h2>
            <div className="overflow-x-auto rounded-2xl border border-rose-100/70 bg-white">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-rose-100 bg-purple-50 text-left text-xs font-bold uppercase text-slate-500">
                  <th className="px-3 py-3">Terapis</th><th className="px-3 py-3">Lokasi</th><th className="px-3 py-3">Pasien</th><th className="px-3 py-3">Treatment</th><th className="px-3 py-3">Omzet</th><th className="px-3 py-3">Diskon</th><th className="px-3 py-3">Omzet Final</th>
                </tr></thead>
                <tbody>
                  {data.rows.length === 0 ? (
                    <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">Belum ada transaksi Spa/Mom&Baby pada periode ini.</td></tr>
                  ) : data.rows.map((r, i) => (
                    <tr key={i} data-testid={`spa-row-${i}`} className="border-b border-rose-50 last:border-0">
                      <td className="px-3 py-3 font-semibold text-slate-800">{r.terapis}</td>
                      <td className="px-3 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${r.lokasi === "Homecare" ? "bg-purple-100 text-purple-700" : "bg-sky-100 text-sky-700"}`}>{r.lokasi}</span></td>
                      <td className="px-3 py-3">{r.jumlah_pasien}</td>
                      <td className="px-3 py-3">{r.jumlah_treatment}</td>
                      <td className="px-3 py-3">{formatRp(r.omzet)}</td>
                      <td className="px-3 py-3 text-amber-700">{formatRp(r.diskon)}</td>
                      <td className="px-3 py-3 font-bold text-emerald-700">{formatRp(r.omzet_final)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
